/**
 * Physical constants for the field-and-force calculators.
 *
 * Values are the NIST CODATA 2022 recommended values
 * (https://physics.nist.gov/cuu/Constants/). Since the 2019 SI revision `c`, `h`, `e` and
 * `k_B` are exact; `G`, `ε₀` and the particle masses are measured and carry the
 * uncertainties noted. `g₀` and the standard atmosphere are exact by definition.
 */

/** Speed of light in vacuum, m/s (exact). */
export const SPEED_OF_LIGHT = 299_792_458;

/** Newtonian constant of gravitation, m³ kg⁻¹ s⁻² — 6.674 30(15) × 10⁻¹¹. */
export const GRAVITATIONAL_CONSTANT = 6.6743e-11;

/** Vacuum electric permittivity ε₀, F/m — 8.854 187 8188(14) × 10⁻¹². */
export const VACUUM_PERMITTIVITY = 8.854_187_818_8e-12;

/** Coulomb constant kₑ = 1 / (4π ε₀), N m² C⁻² — 8.987 551 7862(14) × 10⁹. */
export const COULOMB_CONSTANT = 1 / (4 * Math.PI * VACUUM_PERMITTIVITY);

/** Elementary charge, C (exact). */
export const ELEMENTARY_CHARGE = 1.602_176_634e-19;

/** Electron mass, kg — 9.109 383 7139(28) × 10⁻³¹. */
export const ELECTRON_MASS = 9.109_383_713_9e-31;

/** Proton mass, kg — 1.672 621 925 95(52) × 10⁻²⁷. */
export const PROTON_MASS = 1.672_621_925_95e-27;

/** Atomic mass constant (unified atomic mass unit), kg — 1.660 539 068 92(52) × 10⁻²⁷. */
export const ATOMIC_MASS_UNIT = 1.660_539_068_92e-27;

/** Standard acceleration of gravity g₀, m/s² (exact). */
export const STANDARD_GRAVITY = 9.806_65;

/** Standard atmosphere, Pa (exact). */
export const STANDARD_ATMOSPHERE = 101_325;

/** One tonne of TNT, J — the thermochemical convention (exact). */
export const TNT_TON_JOULES = 4.184e9;

/** Astronomical unit, m (IAU 2012, exact). */
export const ASTRONOMICAL_UNIT = 149_597_870_700;

/** Julian year, s (365.25 days). */
export const JULIAN_YEAR_SECONDS = 365.25 * 86_400;

/** Light-year, m (c × one Julian year). */
export const LIGHT_YEAR = SPEED_OF_LIGHT * JULIAN_YEAR_SECONDS;

/** Bohr radius a₀, m — 5.291 772 105 44(82) × 10⁻¹¹ (CODATA 2022). */
export const BOHR_RADIUS = 5.291_772_105_44e-11;
